import { PlanName } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { navigateInternal } from 'base/navigation';
import { PreferencesStore } from 'base/preferences_store';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { Icon } from 'base/ui/icon';
import { IconCheck } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { TextRegular, TextRegularLight, TextSmall } from 'base/ui/typography';
import { authRouteToUrl } from 'blocks/auth/routes';
import { getHumanPlanName } from 'blocks/billing/plan_utils';
import { observer } from 'mobx-react-lite';
import React from 'react';

export const MaybeLegacy2024PricingModal = observer(
  function _MaybeLegacy2024PricingModal({
    accountStore,
    preferencesStore,
  }: {
    accountStore: AccountStore;
    preferencesStore: PreferencesStore;
  }) {
    if (
      !accountStore.member?.legacyPlanName ||
      preferencesStore.get('dismiss2024PricingAnnouncement')
    ) {
      return null;
    }

    const { planName, legacyPlanName } = accountStore.member;
    if (legacyPlanName == null || planName !== PlanName.PRO) {
      return null;
    }

    const dismissPricingAnnouncement = () => {
      preferencesStore.set('dismiss2024PricingAnnouncement', true);
    };
    const humanPlanName = getHumanPlanName(planName);
    return (
      <Dialog
        title="You’ve been upgraded"
        width="narrow"
        onClose={dismissPricingAnnouncement}
        buttons={
          <>
            <Button
              size="large"
              label="View all plans"
              variant="secondary"
              onClick={(event) => {
                dismissPricingAnnouncement();
                navigateInternal(event);
              }}
              href={authRouteToUrl({ path: 'upgrade', context: {} })}
            />
            <Button
              size="large"
              label="Got it"
              variant="primary"
              onClick={dismissPricingAnnouncement}
            />
          </>
        }
      >
        <TextRegularLight>
          We've updated our pricing plans at Relume, and we're excited to
          announce that all existing {getHumanPlanName(legacyPlanName)} users
          have been upgraded to our {humanPlanName} plan at no additional cost.
          Enjoy the enhanced features on us!
        </TextRegularLight>
        <Rows spacing={8}>
          <TextRegular tag="span">
            Relume{' '}
            <TextRegular tag="span" color="gradient">
              {humanPlanName}
            </TextRegular>
          </TextRegular>

          <div
            style={{
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: COLOR_TOKENS.colorLightGrey3,
            }}
          >
            <FeatureList
              features={[
                { label: 'Unlimited Projects' },
                { label: 'Unlimited Pages' },
                { label: 'Share - Commenting' },
                {
                  label: 'Export - Figma, Webflow & React',
                },
                { label: '1,000+ Webflow components' },
                { label: '1,000+ React components' },
                { label: '1,000+ Figma components' },
              ]}
            />
          </div>
        </Rows>
      </Dialog>
    );
  },
);

function FeatureList({
  features,
}: {
  features: {
    label: string;
    iconsRight?: JSX.Element;
  }[];
}) {
  return (
    <Rows spacing={8}>
      {features.map(({ label, iconsRight }, index) => (
        <Inline key={index} xalign="space-between" yalign="center" spacing={0}>
          <Inline xalign="start" yalign="center" spacing={10}>
            <Icon icon={IconCheck} />
            <TextSmall>{label}</TextSmall>
          </Inline>
          {iconsRight}
        </Inline>
      ))}
    </Rows>
  );
}
